import { DEFAULT_SAFE_AREAS, safeAreaToRect } from '@cinematic-engine/core';
import { useEffect, useRef } from 'react';
import { buildDirectedShots, drawFrame, loadMedia } from '../media/render-engine';
import { useStudio } from '../store/studio-store';

export function Canvas() {
  const project = useStudio((s) => s.project);
  const safeAreasVisible = useStudio((s) => s.safeAreasVisible);
  const canvasZoom = useStudio((s) => s.canvasZoom);
  const setCanvasZoom = useStudio((s) => s.setCanvasZoom);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const width = project?.format.width ?? 1920;
  const height = project?.format.height ?? 1080;
  const displayWidth = width * canvasZoom;
  const displayHeight = height * canvasZoom;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const draw = () => {
      const state = useStudio.getState();
      const fps = state.project?.format.fps ?? state.selectedFormat?.fps ?? 24;
      const time = state.currentFrame / fps;
      drawFrame(ctx, {
        assets: new Map(state.assets.map((a) => [a.id, a])),
        tracks: state.tracks,
        time,
        width: canvas.width,
        height: canvas.height,
        directedShots: buildDirectedShots(state.project?.scenes ?? null),
      });
    };

    let settleRaf = 0;
    let settleFrames = 0;
    let stopped = false;
    const settle = () => {
      if (stopped) return;
      draw();
      settleFrames++;
      if (settleFrames < 120) settleRaf = requestAnimationFrame(settle);
    };
    settleRaf = requestAnimationFrame(settle);

    const unsubscribe = useStudio.subscribe(draw);
    for (const asset of useStudio.getState().assets) {
      void loadMedia(asset).then(draw);
    }

    return () => {
      stopped = true;
      cancelAnimationFrame(settleRaf);
      unsubscribe();
    };
  }, []);

  const titleSafeRect = safeAreaToRect(DEFAULT_SAFE_AREAS['title-safe'], { width, height });
  const actionSafeRect = safeAreaToRect(DEFAULT_SAFE_AREAS['action-safe'], { width, height });
  const subtitleSafeRect = safeAreaToRect(DEFAULT_SAFE_AREAS['subtitle-safe'], { width, height });

  return (
    <div className="canvas-view">
      <div className="canvas-toolbar">
        <span className="canvas-size">
          {width} × {height}
          {project ? ` @ ${project.format.fps}fps` : ''}
        </span>
        <div className="canvas-zoom">
          {[0.25, 0.5, 1].map((z) => (
            <button
              key={z}
              type="button"
              className={`zoom-btn${canvasZoom === z ? ' active' : ''}`}
              onClick={() => setCanvasZoom(z)}
            >
              {z * 100}%
            </button>
          ))}
          <button type="button" className="zoom-btn" onClick={() => setCanvasZoom(1)}>
            Fit
          </button>
        </div>
      </div>

      <div className="canvas-stage">
        <div
          className="canvas-frame"
          style={{
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            position: 'relative',
          }}
        >
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="canvas-render"
            style={{ width: `${displayWidth}px`, height: `${displayHeight}px` }}
          />

          {safeAreasVisible && (
            <div className="safe-overlays">
              <div
                className="safe-title"
                style={{
                  position: 'absolute',
                  ...scaledRect(titleSafeRect, canvasZoom),
                  border: '1px dashed #4ade80',
                }}
              />
              <div
                className="safe-action"
                style={{
                  position: 'absolute',
                  ...scaledRect(actionSafeRect, canvasZoom),
                  border: '1px dashed #facc15',
                }}
              />
              <div
                className="safe-subtitle"
                style={{
                  position: 'absolute',
                  ...scaledRect(subtitleSafeRect, canvasZoom),
                  border: '1px dashed #60a5fa',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function scaledRect(
  rect: { x: number; y: number; width: number; height: number },
  scale: number
): Record<string, string> {
  return {
    left: `${rect.x * scale}px`,
    top: `${rect.y * scale}px`,
    width: `${rect.width * scale}px`,
    height: `${rect.height * scale}px`,
  };
}
